<template>
  <div class="users-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-user"></i>
          用户管理
        </h1>
        <p class="page-description">管理系统用户，包括用户信息查看、编辑、删除等操作</p>
      </div>
    </div>

    <!-- 搜索和操作区域 -->
    <div class="search-section">
      <el-card class="search-card" shadow="never">
        <div class="search-form">
          <div class="search-row">
            <div class="search-item">
              <el-input
                v-model="searchForm.keyword"
                placeholder="搜索用户名或邮箱"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.user_id"
                placeholder="用户ID"
                clearable
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-select
                v-model="searchForm.status"
                placeholder="用户状态"
                clearable
                class="status-select"
              >
                <el-option label="全部状态" value="" />
                <el-option label="正常" :value="1" />
                <el-option label="封禁" :value="0" />
              </el-select>
            </div>
            <div class="search-actions">
              <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                搜索
              </el-button>
              <el-button icon="el-icon-refresh" @click="handleReset">
                重置
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card class="action-card" shadow="never">
        <div class="action-buttons">
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="handleCreate"
            class="create-btn"
          >
            新增用户
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            :disabled="selectedUsers.length === 0"
            @click="handleBatchDelete"
            class="batch-delete-btn"
          >
            批量删除 ({{ selectedUsers.length }})
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card class="table-card" shadow="never">
        <el-table
          :data="userList"
          v-loading="loading"
          @selection-change="handleSelectionChange"
          stripe
          class="user-table"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="id" label="用户ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="username-cell" :title="scope.row.username">
                <el-avatar :size="32" class="user-avatar">
                  {{ scope.row.username.charAt(0).toUpperCase() }}
                </el-avatar>
                <span class="username-text">{{ scope.row.username }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="email" label="邮箱" min-width="200" align="center">
            <template slot-scope="scope">
              <div class="email-cell">
                <i class="el-icon-message"></i>
                <span>{{ scope.row.email }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="150" align="center">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.status"
                :active-value="1"
                :inactive-value="0"
                active-text="正常"
                inactive-text="封禁"
                @change="handleStatusChange(scope.row)"
                :loading="scope.row.statusLoading"
              />
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="注册时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="time-cell">
                <i class="el-icon-time"></i>
                <span>{{ formatDate(scope.row.created_at) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="350" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  type="text"
                  icon="el-icon-view"
                  @click="handleView(scope.row)"
                  class="action-btn view-btn"
                >
                  查看
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-edit"
                  @click="handleEdit(scope.row)"
                  class="action-btn edit-btn"
                >
                  编辑
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="handleResetPassword(scope.row)"
                  class="action-btn reset-btn"
                >
                  重置密码
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  @click="handleDelete(scope.row)"
                  class="action-btn delete-btn"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            class="pagination"
          />
        </div>
      </el-card>
    </div>

    <!-- 用户详情对话框 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="600px"
      class="detail-dialog"
    >
      <div v-if="currentUser" class="user-detail">
        <div class="detail-section">
          <h3>基本信息</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <label>用户ID：</label>
              <span>{{ currentUser.id }}</span>
            </div>
            <div class="detail-item">
              <label>用户名：</label>
              <span>{{ currentUser.username }}</span>
            </div>
            <div class="detail-item">
              <label>邮箱：</label>
              <span>{{ currentUser.email }}</span>
            </div>
            <div class="detail-item">
              <label>状态：</label>
              <el-tag :type="currentUser.status === 1 ? 'success' : 'danger'">
                {{ currentUser.status === 1 ? '正常' : '封禁' }}
              </el-tag>
            </div>
            <div class="detail-item">
              <label>创建时间：</label>
              <span>{{ formatDate(currentUser.created_at) }}</span>
            </div>
            <div class="detail-item">
              <label>更新时间：</label>
              <span>{{ formatDate(currentUser.updated_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 新增/编辑用户对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="formDialogVisible"
      width="500px"
      class="form-dialog"
    >
      <el-form
        :model="userForm"
        :rules="formRules"
        ref="userForm"
        label-width="80px"
        class="user-form"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input
            v-model="userForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="userForm.status">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">封禁</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, createUser, updateUser, deleteUser, batchDeleteUsers, resetUserPassword } from '@/api/user'

export default {
  name: 'AdminUsers',
  data() {
    return {
      loading: false,
      userList: [],
      selectedUsers: [],
      searchForm: {
        keyword: '',
        user_id: '',
        status: ''
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      formDialogVisible: false,
      isEdit: false,
      currentUser: null,
      userForm: {
        username: '',
        email: '',
        password: '',
        status: 1
      },
      formRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择用户状态', trigger: 'change' }
        ]
      },
      submitLoading: false
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑用户' : '新增用户'
    }
  },
  created() {
    this.getUserList()
  },
  methods: {
    // 获取用户列表
    async getUserList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          keyword: this.searchForm.keyword,
          user_id: this.searchForm.user_id,
          status: this.searchForm.status
        }
        const response = await getUserList(params)
        this.userList = response.data.list.map(user => ({
          ...user,
          statusLoading: false
        }))
        this.pagination.total = response.data.total
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.getUserList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        keyword: '',
        user_id: '',
        status: ''
      }
      this.pagination.page = 1
      this.getUserList()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.getUserList()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.page = page
      this.getUserList()
    },

    // 选择改变
    handleSelectionChange(selection) {
      this.selectedUsers = selection
    },

    // 状态改变
    async handleStatusChange(user) {
      user.statusLoading = true
      try {
        await updateUser(user.id, { status: user.status })
        this.$message.success('状态更新成功')
      } catch (error) {
        // 恢复原状态
        user.status = user.status === 1 ? 0 : 1
      } finally {
        user.statusLoading = false
      }
    },

    // 查看用户详情
    async handleView(user) {
      this.currentUser = user
      this.detailDialogVisible = true
    },

    // 新增用户
    handleCreate() {
      this.isEdit = false
      this.userForm = {
        username: '',
        email: '',
        password: '',
        status: 1
      }
      this.formDialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm.clearValidate()
      })
    },

    // 编辑用户
    handleEdit(user) {
      this.isEdit = true
      this.userForm = {
        username: user.username,
        email: user.email,
        password: '',
        status: user.status
      }
      this.currentUser = user
      this.formDialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm.clearValidate()
      })
    },

    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.userForm.validate()
        this.submitLoading = true

        if (this.isEdit) {
          await updateUser(this.currentUser.id, this.userForm)
          this.$message.success('用户更新成功')
        } else {
          await createUser(this.userForm)
          this.$message.success('用户创建成功')
        }

        this.formDialogVisible = false
        this.getUserList()
      } finally {
        this.submitLoading = false
      }
    },

    // 删除用户
    handleDelete(user) {
      this.$confirm(`确定要删除用户 "${user.username}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteUser(user.id)
        this.$message.success('删除成功')
        this.getUserList()
      })
    },

    // 批量删除
    handleBatchDelete() {
      if (this.selectedUsers.length === 0) {
        this.$message.warning('请选择要删除的用户')
        return
      }

      this.$confirm(`确定要删除选中的 ${this.selectedUsers.length} 个用户吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const ids = this.selectedUsers.map(user => user.id)
        await batchDeleteUsers({ ids })
        this.$message.success('批量删除成功')
        this.getUserList()
      })
    },

    // 重置密码
    handleResetPassword(user) {
      this.$confirm(`确定要重置用户 "${user.username}" 的密码吗？新密码将为 123456`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await resetUserPassword(user.id)
        this.$message.success('密码重置成功，新密码为：123456')
      })
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.users-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  margin-bottom: 20px;
}

.header-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px;
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-title {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-description {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-card {
  border-radius: 12px;
  border: none;
}

.search-form {
  padding: 10px 0;
}

.search-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-item {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
}

.status-select {
  width: 100%;
}

.search-actions {
  display: flex;
  gap: 12px;
}

/* 操作区域 */
.action-section {
  margin-bottom: 20px;
}

.action-card {
  border-radius: 12px;
  border: none;
}

.action-section .action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.create-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

.batch-delete-btn {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 20px;
}

.table-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.user-table {
  width: 100%;
}

.username-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-avatar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.username-text {
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.email-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
}

.time-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 14px;
}

.table-section .action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-btn {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  transition: all 0.3s;
}

.view-btn {
  color: #409eff;
}

.view-btn:hover {
  background-color: #ecf5ff;
  color: #337ecc;
}

.edit-btn {
  color: #67c23a;
}

.edit-btn:hover {
  background-color: #f0f9ff;
  color: #529b2e;
}

.reset-btn {
  color: #e6a23c;
}

.reset-btn:hover {
  background-color: #fdf6ec;
  color: #b88230;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  background-color: #fef0f0;
  color: #c45656;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.pagination {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 对话框 */
.detail-dialog .el-dialog__body {
  padding: 20px;
}

.user-detail {
  padding: 20px 0;
}

.detail-section h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 10px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.detail-item label {
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
  min-width: 80px;
}

.detail-item span {
  color: #303133;
}

.form-dialog .el-dialog__body {
  padding: 20px;
}

.user-form {
  padding: 20px 0;
}

.dialog-footer {
  text-align: right;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .users-container {
    padding: 10px;
  }

  .page-title {
    font-size: 24px;
  }

  .search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-item {
    min-width: auto;
  }

  .search-actions {
    justify-content: center;
  }

  .action-section .action-buttons {
    justify-content: flex-start;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .table-section .action-buttons {
    flex-direction: column;
    gap: 4px;
  }

  .action-btn {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 20px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-description {
    font-size: 14px;
  }

  .el-table {
    font-size: 14px;
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}
</style>
